import type { AuthPermission } from '../api/generated/models/AuthPermission';

export type PermissionMode = 'all' | 'any';

export type AppModuleNavItem = {
  label: string;
  path: string;
  exact: boolean;
  icon: string;
  eyebrow: string;
  description: string;
  status: 'Disponível' | 'Planejado';
  permissions?: readonly AuthPermission[];
  permissionMode?: PermissionMode;
  placeholder?: boolean;
  children?: readonly AppModuleNavItem[];
};

const CONVERSATION_VISIBILITY_PERMISSIONS = [
  'conversations:base:visibility:all',
  'conversations:base:visibility:team',
  'conversations:base:visibility:own',
] satisfies readonly AuthPermission[];

export const APP_MODULE_GROUPS: readonly (readonly AppModuleNavItem[])[] = [
  [
    {
      label: 'Dashboard',
      path: '/dashboard',
      exact: false,
      icon: 'heroSquares2x2',
      eyebrow: 'Visão geral',
      description: 'Resumo operacional, prioridades, métricas, alertas e próximos movimentos.',
      status: 'Disponível',
      permissions: ['platform:read'],
    },
    {
      label: 'Marketing',
      path: '/marketing/estrategia',
      exact: false,
      icon: 'heroMegaphone',
      eyebrow: 'Aquisição e campanhas',
      description: 'Estratégia, conteúdo, campanhas, captura de leads e mídia paga.',
      status: 'Planejado',
      permissions: ['platform:read'],
      children: [
        navChild('Estratégia', '/marketing/estrategia', 'Posicionamento, ICP, proposta de valor e plano de comunicação.', 'heroFlag', ['platform:read']),
        navChild('Conteúdo', '/marketing/conteudo', 'Planejamento, geração e biblioteca de conteúdo.', 'heroDocumentText', ['platform:read']),
        navChild('Campanhas', '/marketing/campanhas', 'Disparos multicanal para segmentos de contatos, com controles anti-banimento.', 'heroMegaphone', ['marketing:campaigns:read'], 'Disponível', false),
        navChild('Captura', '/marketing/captura', 'Formulários, submissões, landing pages e origem dos leads.', 'heroListBullet', ['platform:read']),
        navChild('Mídia paga', '/marketing/midia-paga', 'Métricas operacionais, criativos e acompanhamento de mídia.', 'heroChartBarSquare', ['marketing:paid_media:read']),
        navChild('Análise de mídia paga', '/marketing/analise', 'Análise de campanhas com IA: diagnóstico e próximos passos.', 'heroSparkles', ['marketing:analysis:read']),
        navChild('Análise de conteúdo', '/marketing/analise-conteudo', 'Padrões vencedores do seu perfil + ideias de criativo e copy com IA.', 'heroSparkles', ['marketing:content-analysis:read']),
      ],
    },
  ],
  [
    {
      label: 'CRM',
      path: '/crm/hoje',
      exact: false,
      icon: 'heroUsers',
      eyebrow: 'Relacionamento',
      description: 'Hoje, contatos, empresas, oportunidades, segmentos e preferências.',
      status: 'Disponível',
      permissions: ['crm:read'],
      children: [
        navChild('Hoje', '/crm/hoje', 'Tarefas do dia, follow-ups e resumo do funil.', 'heroSun', ['crm:read']),
        navChild('Contatos', '/crm/contatos', 'Pessoas, leads, clientes e identidades de canal.', 'heroUsers', ['crm:read']),
        navChild('Duplicatas', '/crm/duplicatas', 'Revisar e mesclar contatos possivelmente duplicados.', 'heroUsers', ['crm:contacts:merge']),
        navChild('Agenda', '/crm/agenda', 'Calendário geral da plataforma: eventos, recorrência e vínculo com contatos.', 'heroCalendarDays', ['crm:agenda:read']),
        navChild('Empresas', '/crm/empresas', 'Organizações B2B vinculadas a contatos e oportunidades.', 'heroBuildingOffice2', ['crm:read']),
        navChild('Oportunidades', '/crm/oportunidades', 'Oportunidades comerciais e processo de venda.', 'heroBanknotes', ['crm:read']),
        navChild('Metas', '/crm/metas', 'Metas de vendas: meta, realizado e forecast por vendedor.', 'heroChartBarSquare', ['crm:read']),
        navChild('Segmentos', '/crm/segmentos', 'Filtros salvos usados por agentes, automações e campanhas.', 'heroFunnel', ['crm:read']),
        navChild('Preferências', '/crm/preferencias', 'Opt-in, opt-out, supressão e evidências de comunicação.', 'heroShieldCheck', ['crm:read']),
      ],
    },
    {
      label: 'Conversas',
      path: '/conversas/visao-geral',
      exact: false,
      icon: 'heroChatBubbleLeftRight',
      eyebrow: 'Operação de canais',
      description: 'Inbox, conversas ativas, pendências, preferências e handoff.',
      status: 'Disponível',
      permissions: CONVERSATION_VISIBILITY_PERMISSIONS,
      permissionMode: 'any',
      children: [
        navChild(
          'Visão Geral',
          '/conversas/visao-geral',
          'Inbox completo e acompanhamento das conversas ativas.',
          'heroChatBubbleLeftRight',
          CONVERSATION_VISIBILITY_PERMISSIONS,
          'Disponível',
          false,
          'any',
          false,
        ),
        navChild(
          'Meus atendimentos',
          '/conversas/meus-atendimentos',
          'Conversas atribuídas ao usuário atual.',
          'heroUser',
          CONVERSATION_VISIBILITY_PERMISSIONS,
          'Disponível',
          false,
          'any',
          false,
        ),
        navChild(
          'Aguardando',
          '/conversas/aguardando',
          'Conversas pendentes de resposta ou continuidade.',
          'heroClock',
          CONVERSATION_VISIBILITY_PERMISSIONS,
          'Disponível',
          false,
          'any',
          false,
        ),
        navChild(
          'Chamadas',
          '/conversas/chamadas',
          'Histórico de ligações, dialer manual e detalhes (áudio + transcrição).',
          'heroPhone',
          CONVERSATION_VISIBILITY_PERMISSIONS,
          'Disponível',
          false,
          'any',
          false,
        ),
        navChild(
          'Análises',
          '/conversas/analises',
          'Sentimento, objeções, métricas e recomendações estratégicas das conversas.',
          'heroSparkles',
          ['conversations:analysis:read'],
          'Disponível',
          false,
          'all',
          false,
        ),
        navChild(
          'Preferências',
          '/conversas/preferencias',
          'Preferências, labels, SLA e regras de operação das conversas.',
          'heroCog6Tooth',
          CONVERSATION_VISIBILITY_PERMISSIONS,
          'Disponível',
          false,
          'any',
          false,
        ),
      ],
    },
  ],
  [
    {
      label: 'Receita',
      path: '/vendas',
      exact: false,
      icon: 'heroShoppingBag',
      eyebrow: 'Operação comercial',
      description: 'Produtos e vendas.',
      status: 'Planejado',
      permissions: ['revenue:products:read'],
      children: [
        navChild('Produtos', '/produtos', 'Produtos, serviços, pacotes e disponibilidade comercial.', 'heroShoppingBag', ['revenue:products:read'], 'Disponível', false, 'all', false),
        navChild('Vendas', '/vendas', 'Vendas, carrinhos, propostas e estado comercial.', 'heroBanknotes', ['revenue:sales:read'], 'Disponível', false, 'all', false),
      ],
    },
  ],
  [
    {
      label: 'Agentes',
      path: '/agentes',
      exact: false,
      icon: 'heroCpuChip',
      eyebrow: 'Automação assistida',
      description: 'Agentes, playground e automações.',
      status: 'Disponível',
      permissions: ['agents:read'],
      children: [
        navChild('Agentes', '/agentes', 'Identidade, versões, canais, conhecimento, metas e publicação.', 'heroCpuChip', ['agents:read'], 'Disponível', false),
        navChild('Arquétipos', '/agentes/arquetipos', 'Fluxos, modelos, prompts e políticas versionadas para novos agentes.', 'heroSquares2x2', ['agents:read'], 'Disponível', false),
        navChild('Playground', '/agentes/playground', 'Ambiente para testar comportamento, prompts e contexto antes de publicar.', 'heroBeaker', ['agents:read']),
        navChild('Automações', '/agentes/automacoes', 'Workflows vivos, gatilhos, steps e execução explícita.', 'heroBolt', ['agents:read'], 'Disponível', false),
      ],
    },
    {
      label: 'Biblioteca',
      path: '/biblioteca',
      exact: false,
      icon: 'heroBookOpen',
      eyebrow: 'Base de recuperação',
      description: 'Bases, fontes, documentos estruturados, ingestão e políticas de acesso.',
      status: 'Disponível',
      permissions: ['knowledge:bases:read'],
      children: [
        navChild('Bibliotecas', '/biblioteca', 'Bases de conhecimento e documentos recuperáveis.', 'heroBookOpen', ['knowledge:bases:read'], 'Disponível', false),
        navChild('Fontes', '/biblioteca/fontes', 'Fontes conectadas, origem e sincronização.', 'heroQueueList', ['knowledge:sources:read']),
      ],
    },
    {
      label: 'Integrações',
      path: '/integracoes',
      exact: false,
      icon: 'heroLink',
      eyebrow: 'Conexões técnicas',
      description: 'Provedores, credenciais, webhooks, contas externas, templates e mapeamentos.',
      status: 'Disponível',
      permissions: ['integrations:connections:read'],
      children: [
        navChild('Conexoes', '/integracoes/conexoes', 'Provedores, credenciais, webhooks, templates e saude.', 'heroLink', ['integrations:connections:read'], 'Disponível', false),
        navChild('Meus Canais', '/integracoes/canais', 'Contas de canal conectadas e vinculos operacionais.', 'heroPhone', ['integrations:channel-accounts:read'], 'Disponível', false),
      ],
    },
  ],
];

export const APP_MODULES: readonly AppModuleNavItem[] = APP_MODULE_GROUPS.flat();
export const APP_NAV_ITEMS: readonly AppModuleNavItem[] = flattenNavItems(APP_MODULES);

export const SETTINGS_NAV_ITEM: AppModuleNavItem = {
  label: 'Configurações',
  path: '/configuracoes',
  exact: false,
  icon: 'heroCog6Tooth',
  eyebrow: 'Administração',
  description: 'Business, membros, permissões, times, chaves, auditoria e preferências.',
  status: 'Disponível',
};

export const SETTINGS_MODULES: readonly AppModuleNavItem[] = [
  settingsChild('Geral', '/configuracoes/geral', 'Dados da organização, preferências operacionais e padrões do negócio.', 'heroCog6Tooth', [
    'core:business:read',
  ]),
  settingsChild('Usuários', '/configuracoes/usuarios', 'Membros, convites e vínculos de acesso da organização.', 'heroUsers', [
    'core:memberships:read',
  ]),
  settingsChild('Equipes', '/configuracoes/equipes', 'Times, composição operacional e distribuição de responsabilidades.', 'heroUserGroup', [
    'core:teams:read',
  ]),
  settingsChild('Assinatura', '/configuracoes/assinatura', 'Plano, cobrança e limites comerciais da organização.', 'heroCreditCard', ['billing:subscription:read']),
  settingsChild('Consumo', '/configuracoes/uso-e-quotas', 'Quanto sua operação gastou neste ciclo, por área — e o teto de cada uma.', 'heroChartBarSquare', ['billing:usage:read']),
  settingsChild('Cobrança', '/configuracoes/cobranca', 'Saldo de créditos, recargas, forma de pagamento e faturas da mensalidade.', 'heroBanknotes', ['billing:wallet:read']),
  settingsChild('Permissões', '/configuracoes/permissoes', 'Papéis, políticas de acesso e permissões administrativas.', 'heroShieldCheck', [
    'core:roles:read',
  ]),
  settingsChild('Service Accounts', '/configuracoes/service-accounts', 'Credenciais técnicas, chaves de API e acessos de integração.', 'heroKey', [
    'core:service-accounts:read',
  ]),
  settingsChild('Perfil', '/configuracoes/perfil', 'Dados pessoais, avatar, senha e organizações vinculadas.', 'heroIdentification'),
];
export const ROUTE_MODULES: readonly AppModuleNavItem[] = APP_NAV_ITEMS.filter((item) => item.placeholder);
export const PLACEHOLDER_ROUTE_MODULES = ROUTE_MODULES;

function navChild(
  label: string,
  path: string,
  description: string,
  icon: string,
  permissions?: readonly AuthPermission[],
  status: AppModuleNavItem['status'] = 'Planejado',
  placeholder = true,
  permissionMode: PermissionMode = 'all',
  exact = true,
): AppModuleNavItem {
  return {
    label,
    path,
    exact,
    icon,
    eyebrow: label,
    description,
    status,
    permissions,
    permissionMode,
    placeholder,
  };
}

function settingsChild(
  label: string,
  path: string,
  description: string,
  icon: string,
  permissions?: readonly AuthPermission[],
): AppModuleNavItem {
  return {
    label,
    path,
    exact: true,
    icon,
    eyebrow: label,
    description,
    status: 'Disponível',
    permissions,
    placeholder: false,
  };
}

function flattenNavItems(items: readonly AppModuleNavItem[]): readonly AppModuleNavItem[] {
  return items.flatMap((item) => [item, ...(item.children ? flattenNavItems(item.children) : [])]);
}
